import React, { useState, useEffect, useCallback } from 'react';
import { useForegroundInterval } from '../services/AppStateService';

/**
 * A real-time countdown component that displays time remaining in a grace period.
 * Updates every second for a live countdown experience.
 * 
 * @param {Date|string} firstViolationAt - When the violation started
 * @param {number} gracePeriodDays - Total grace period in days
 * @param {string} expiredText - Text to show when expired (default: "Grace period expired!")
 * @param {boolean} showSeconds - Whether to show seconds (default: true)
 */
const GracePeriodCountdown = ({ 
  firstViolationAt, 
  gracePeriodDays = 7, 
  expiredText = "Grace period expired!",
  showSeconds = true
}) => {
  const [timeRemaining, setTimeRemaining] = useState(null);

  const calculateRemaining = useCallback(() => {
    if (!firstViolationAt) {
      // No violation yet, show full grace period
      return {
        days: gracePeriodDays,
        hours: 0,
        minutes: 0,
        seconds: 0,
        totalMs: gracePeriodDays * 24 * 60 * 60 * 1000,
        expired: false
      };
    }

    const violationStart = new Date(firstViolationAt);
    const gracePeriodMs = gracePeriodDays * 24 * 60 * 60 * 1000;
    const expiresAt = new Date(violationStart.getTime() + gracePeriodMs);
    const now = new Date();
    const remainingMs = expiresAt.getTime() - now.getTime();

    if (remainingMs <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        totalMs: 0,
        expired: true
      };
    }

    const days = Math.floor(remainingMs / (24 * 60 * 60 * 1000));
    const hours = Math.floor((remainingMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((remainingMs % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((remainingMs % (60 * 1000)) / 1000);

    return {
      days,
      hours,
      minutes,
      seconds,
      totalMs: remainingMs,
      expired: false
    };
  }, [firstViolationAt, gracePeriodDays]);

  const refresh = useCallback(() => {
    setTimeRemaining(calculateRemaining());
  }, [calculateRemaining]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useForegroundInterval(refresh, 1000);

  if (!timeRemaining) return null;

  if (timeRemaining.expired) {
    return <span className="countdown-expired">{expiredText}</span>;
  }

  // Format the display
  const parts = [];
  if (timeRemaining.days > 0) {
    parts.push(`${timeRemaining.days}d`);
  }
  if (timeRemaining.hours > 0 || timeRemaining.days > 0) {
    parts.push(`${timeRemaining.hours}h`);
  }
  if (timeRemaining.minutes > 0 || timeRemaining.hours > 0 || timeRemaining.days > 0) {
    parts.push(`${timeRemaining.minutes}m`);
  }
  if (showSeconds) {
    parts.push(`${timeRemaining.seconds}s`);
  }

  return <span className="countdown-timer">{parts.join(' ')}</span>;
};

export default GracePeriodCountdown;
